'use client';

import type { QueryClient } from '@tanstack/react-query';
import type { CreditBalanceResult } from './fetch-credit-balance';

export const CREDITS_REFRESH_EVENT = 'credits:refresh';
export const CREDITS_OPTIMISTIC_RESERVE_EVENT = 'credits:optimistic-reserve';
export const CREDITS_OPTIMISTIC_RELEASE_EVENT = 'credits:optimistic-release';

const CREDITS_QUERY_KEY = ['credits'] as const;
const CREDITS_BALANCE_QUERY_KEY = ['credits', 'balance'] as const;

function normalizeCreditAmount(amount: number) {
  return Number.isFinite(amount) && amount > 0 ? amount : 0;
}

function updateOptimisticReservation(
  queryClient: QueryClient,
  amount: number,
  direction: 'reserve' | 'release'
) {
  const normalizedAmount = normalizeCreditAmount(amount);
  if (normalizedAmount <= 0) {
    return;
  }

  queryClient.setQueryData<CreditBalanceResult>(
    CREDITS_BALANCE_QUERY_KEY,
    (current) => {
      if (!current) {
        return current;
      }

      const reservedCredits =
        direction === 'reserve'
          ? current.reservedCredits + normalizedAmount
          : Math.max(0, current.reservedCredits - normalizedAmount);
      const availableCredits =
        direction === 'reserve'
          ? Math.max(0, current.availableCredits - normalizedAmount)
          : Math.min(
              current.currentCredits,
              current.availableCredits + normalizedAmount
            );

      return {
        ...current,
        reservedCredits,
        availableCredits,
        balance: availableCredits,
      };
    }
  );
}

export async function refreshCreditsQueries(queryClient: QueryClient) {
  await queryClient.invalidateQueries({
    queryKey: CREDITS_QUERY_KEY,
    refetchType: 'active',
  });
}

export function applyOptimisticCreditReservation(
  queryClient: QueryClient,
  amount: number
) {
  updateOptimisticReservation(queryClient, amount, 'reserve');
}

export function releaseOptimisticCreditReservation(
  queryClient: QueryClient,
  amount: number
) {
  updateOptimisticReservation(queryClient, amount, 'release');
}

export function requestCreditsRefresh() {
  if (typeof window === 'undefined') {
    return;
  }

  window.dispatchEvent(new Event(CREDITS_REFRESH_EVENT));
}

export function requestOptimisticCreditReservation(amount: number) {
  const normalizedAmount = normalizeCreditAmount(amount);
  if (typeof window === 'undefined' || normalizedAmount <= 0) {
    return;
  }

  window.dispatchEvent(
    new CustomEvent<number>(CREDITS_OPTIMISTIC_RESERVE_EVENT, {
      detail: normalizedAmount,
    })
  );
}

export function requestOptimisticCreditRelease(amount: number) {
  const normalizedAmount = normalizeCreditAmount(amount);
  if (typeof window === 'undefined' || normalizedAmount <= 0) {
    return;
  }

  window.dispatchEvent(
    new CustomEvent<number>(CREDITS_OPTIMISTIC_RELEASE_EVENT, {
      detail: normalizedAmount,
    })
  );
}
